import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import IconButton from '../components/IconButton';

const SpinLoader = (props) => <Loader2 className={`${props.className} animate-spin`} />;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  const { forgotPassword } = useAuth();

  const handleChange = (e) => {
    setEmail(e.target.value);
    
    // Limpar erro quando usuário começar a digitar
    if (errors.email) {
      setErrors({});
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!email) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await forgotPassword(email);
      
      if (result.success) {
        setEmailSent(true);
      } else {
        setErrors({ general: result.message || 'Erro ao enviar email' });
      }
    } catch (error) {
      setErrors({ general: 'Erro interno do servidor' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-3 rounded-2xl overflow-hidden shadow-2xl">
          {/* Esquerda: Cartão de confirmação */}
          <div className="bg-white p-10 sm:p-12 lg:col-span-2">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Email enviado!</h2>
              <p className="text-gray-600 mb-6">Enviamos um link de recuperação para <strong>{email}</strong>. Verifique sua caixa de entrada e spam.</p>
              <div className="space-y-4">
                <Link to="/login" className="btn btn-primary w-full py-3 text-base font-medium">Voltar ao Login</Link>
                <div className="flex justify-center">
                  <IconButton
                    type="button"
                    icon={Send}
                    variant="primary"
                    title="Enviar novamente"
                    onClick={() => { setEmailSent(false); setEmail(''); }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Direita: Painel com gradiente */}
          <div className="hidden lg:flex bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white items-center justify-center relative lg:col-span-1">
            <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-white/10 blur-xl" />
            <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-black/10 blur-2xl" />
            <div className="relative z-10 text-center p-10">
              <h3 className="text-3xl font-extrabold tracking-wide">RECUPERAR ACESSO</h3>
              <p className="mt-4 text-sm sm:text-base text-white/80">Siga as instruções enviadas ao seu email para redefinir a senha</p>
              <Link to="/login" className="mt-8 inline-block btn btn-secondary">Voltar ao login</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-3 rounded-2xl overflow-hidden shadow-2xl">
        {/* Esquerda: Cartão de formulário */}
        <div className="bg-white p-10 sm:p-12 lg:col-span-2">
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Esqueceu sua senha?</h2>
            <p className="text-gray-600">Digite seu email e enviaremos um link para redefinir sua senha</p>
          </div>

          {/* Form */}
          <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
            {/* Erro geral */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {errors.general}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={handleChange}
                  className={`input pl-10 ${errors.email ? 'border-red-300 focus:ring-red-500' : ''}`}
                  placeholder="seu@email.com"
                />
              </div>
              {errors.email && (<p className="mt-1 text-sm text-red-600">{errors.email}</p>)}
            </div>

            {/* Submit button */}
            <div className="flex justify-center">
              <IconButton
                type="submit"
                icon={isSubmitting ? SpinLoader : Send}
                variant="primary"
                disabled={isSubmitting}
                title={isSubmitting ? 'Enviando...' : 'Enviar link de recuperação'}
              />
            </div>
          </form>

          {/* Back to login */}
          <div className="text-center">
            <Link to="/login" className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Voltar ao login
            </Link>
          </div>
        </div>

        {/* Direita: Painel com gradiente */}
        <div className="hidden lg:flex bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white items-center justify-center relative lg:col-span-1">
          <div className="absolute -top-16 -left-16 w-48 h-48 rounded-full bg-white/10 blur-xl" />
          <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-black/10 blur-2xl" />
          <div className="relative z-10 text-center p-10">
            <h3 className="text-3xl font-extrabold tracking-wide">RECUPERAR ACESSO</h3>
            <p className="mt-4 text-sm sm:text-base text-white/80">Informe seu email e enviaremos instruções de recuperação</p>
            <Link to="/login" className="mt-8 inline-block btn btn-secondary">Voltar ao login</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;